import {
  resolveCanonicalTreeRelationshipCreationPolicy,
} from '@/governor/tree/CanonicalTreeRelationshipCreationPolicy';
import {
  planCanonicalTreeAncestryReconnection,
} from '@/governor/tree/CanonicalTreeAncestryReconnectionAuthority';
import {
  resolveCanonicalTreeGenerationAnchorAuthority,
  selectCanonicalTreeGenerationAnchorCandidates,
} from '@/governor/tree/CanonicalTreeTopologyAuthority';
import { emitGovernorLiveAction } from '@/lib/governorEventBus';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { toast } from '@/components/ui/sonner';
import {
  GraphMember,
  EXTENDED_RELATIONSHIP_OPTIONS,
  findExtendedOption,
  buildRelationshipWorkflow,
  Relationship,
  Side,
  useFamilyGraph,
  kinshipLabel,
} from '@/contexts/FamilyGraphContext';

import { Loader2, Link2, AlertTriangle, Heart, Sparkles } from 'lucide-react';
import MemberAvatar from './MemberAvatar';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: GraphMember | null;
}

/**
 * "Connect to your tree" modal — UNIFIED RELATIONSHIP-ENGINE EDITION
 * (May 2026 task: "Unify Connect modal with the modern kinship engine").
 *
 * Source of truth = `EXTENDED_RELATIONSHIP_OPTIONS` from
 * FamilyGraphContext. The same list powers:
 *   • the quick-add menu in FamilyTreeView
 *   • AddGraphMemberModal's relationship dropdown
 *   • PersonModal / RelativeRoster relationship badges
 *   • computeKinship() / kinshipLabel() in the engine
 *
 * Critical architecture rule: anywhere the engine can DERIVE a
 * modern-family in-law label (Son-in-law, Daughter-in-law,
 * Brother-in-law, Sister-in-law, Mother-in-law, Father-in-law,
 * Step-Parent, Step-Child, Co-parent, etc.), this Connect modal must
 * EXPOSE that exact same label as a selectable option — and selecting
 * it must produce the structurally correct edge:
 *
 *   • Son-in-law / Daughter-in-law / Brother-in-law / Sister-in-law /
 *     plain Spouse-Partner → coreRelationship='spouse', save anchors
 *     the row's `spouse_id` to the picked partner via `linkSpouses`.
 *     NEVER writes a parent_ids edge to the viewer. NEVER creates a
 *     direct spouse relation between the viewer and the in-law.
 *
 *   • Mother-in-law / Father-in-law / Step-Parent / Step-Child / etc.
 *     are stored as the closest core relationship + `displayOverride`
 *     so the kinship engine returns the in-law title verbatim from the
 *     viewer's perspective.
 *
 *   • Bloodline relationships (mother / father / child / sibling /
 *     cousin / aunt-uncle / grandparent / great-grandparent /
 *     cousin-child) keep the original strict parent_ids logic — that
 *     code is the foundation for every derived label and is
 *     deliberately UNCHANGED by this task.
 *
 * Pure additive: no schema change, no RLS change, no edge function
 * change, no auth/Stripe/uploads/memories touching. Only the connect
 * UI is widened to mirror the engine's full vocabulary.
 */
const SetRelationshipModal: React.FC<Props> = ({ open, onOpenChange, member }) => {
  const {
    updateMember,
    linkParents,
    linkSpouses,
    members,
    viewerMemberId,
    refresh,
  } = useFamilyGraph();

  /**
   * Resolve YOU. Same fallback ladder as AddGraphMemberModal /
   * FamilyGraphSection — strict claimed viewerMemberId first, then the
   * implicit `side === 'self'` anchor row that graph owners get for
   * free without claiming. Without this, every Mother/Father save
   * silently aborted for graph owners.
   */
  const effectiveViewerId = useMemo<string | null>(() => {
    if (viewerMemberId) return viewerMemberId;
    const anchor = members.find(
      (m) =>
        m.side === 'self' &&
        (m.relationship === 'unknown' || (m.relationship as string) === 'self') &&
        m.approvalStatus !== 'rejected',
    );
    return anchor?.id ?? null;
  }, [viewerMemberId, members]);

  // ─── EXTENDED-OPTIONS STATE — same vocabulary as the engine ─────────────
  // `selectedExtendedKey` is the dropdown's current key (core enum value
  // OR a virtual `v_…` key like `v_son_in_law`). The structural
  // `relationship` (used for parent_ids / spouse_id placement) and the
  // visible `customLabel` (written to `relationship_override`) are
  // derived from this key. Mirrors AddGraphMemberModal exactly so both
  // surfaces speak the same language.
  const [selectedExtendedKey, setSelectedExtendedKey] = useState<string>('cousin');
  const [customLabel, setCustomLabel] = useState<string>('');
  const [relationship, setRelationship] = useState<Relationship>('cousin');
  const [side, setSide] = useState<Side>('both');
  const [connectorId, setConnectorId] = useState<string>('');
  const [secondConnectorId, setSecondConnectorId] = useState<string>('');
  const [saving, setSaving] = useState(false);

  /**
   * Apply an EXTENDED_RELATIONSHIP_OPTIONS entry to the form state.
   * Sets structural relationship (core enum), visible label, and
   * resets connector picks because the candidate pool is relationship-
   * specific. Mirrors AddGraphMemberModal's `applyExtendedKey`.
   */
  const applyExtendedKey = (key: string) => {
    const option = EXTENDED_RELATIONSHIP_OPTIONS.find((o) => o.value === key);
    if (!option) {
      setSelectedExtendedKey('unknown');
      setRelationship('unknown');
      setCustomLabel(key);
      setConnectorId('');
      setSecondConnectorId('');
      return;
    }
    setSelectedExtendedKey(option.value);
    setRelationship(option.coreRelationship);
    setCustomLabel(option.displayOverride ?? '');
    setConnectorId('');
    setSecondConnectorId('');
  };

  useEffect(() => {
    if (open && member) {
      // Try to map the existing row's stored relationship/override back
      // to a concrete extended key so the dropdown shows the right entry.
      const fromOverride = findExtendedOption(member.relationshipOverride ?? null);
      if (fromOverride) {
        setSelectedExtendedKey(fromOverride.value);
        setRelationship(fromOverride.coreRelationship);
        setCustomLabel(member.relationshipOverride?.trim() || fromOverride.displayOverride || '');
      } else {
        const core = EXTENDED_RELATIONSHIP_OPTIONS.find(
          (o) => o.value === member.relationship,
        );
        if (core) {
          setSelectedExtendedKey(core.value);
          setRelationship(core.coreRelationship);
          setCustomLabel(core.displayOverride ?? '');
        } else {
          setSelectedExtendedKey('cousin');
          setRelationship('cousin');
          setCustomLabel('');
        }
      }
      setSide(member.side);
      // Pre-fill connector from existing parent_ids when possible OR
      // from spouse_id when this is a spouse-coreRelationship row.
      const existingParents = (member.parentIds ?? []).filter((p) =>
        members.some((m) => m.id === p),
      );
      if (member.spouseId && member.relationship === 'spouse') {
        setConnectorId(member.spouseId);
        setSecondConnectorId('');
      } else {
        setConnectorId(existingParents[0] ?? '');
        setSecondConnectorId(existingParents[1] ?? '');
      }
    }
  }, [open, member, members]);

  // ─── Niece/Nephew detection (cousin-child + niece-keyed override) ──────
  // Mirrors AddGraphMemberModal so picking "Niece / Nephew" surfaces
  // siblings (not cousins) in the connector pool.
  const isNieceNephewMode = useMemo(() => {
    if (relationship !== 'cousin-child') return false;
    if (
      selectedExtendedKey === 'v_niece_nephew' ||
      selectedExtendedKey === 'v_great_niece_nephew'
    ) {
      return true;
    }
    const lc = (customLabel ?? '').toLowerCase();
    return lc.includes('niece') || lc.includes('nephew');
  }, [relationship, selectedExtendedKey, customLabel]);

  // ─── In-law variant detection ─────────────────────────────────────────
  // For coreRelationship='spouse' rows, the customLabel decides whether
  // the connector picker should be filtered to "viewer's children"
  // (son-in-law / daughter-in-law) or "viewer's siblings" (brother /
  // sister-in-law). Plain "Spouse / Partner" surfaces every live row.
  const inLawMode = useMemo<'son-daughter' | 'brother-sister' | 'parent' | 'plain'>(() => {
    const lc = (customLabel ?? '').toLowerCase();
    if (lc.includes('son-in-law') || lc.includes('daughter-in-law')) return 'son-daughter';
    if (lc.includes('brother-in-law') || lc.includes('sister-in-law')) return 'brother-sister';
    if (lc.includes('mother-in-law') || lc.includes('father-in-law') || lc.includes('parent-in-law')) return 'parent';
    return 'plain';
  }, [customLabel]);

  /**
   * Connector candidate pool — context-aware, mirrors AddGraphMemberModal.
   * Rules summary:
   *   bloodline   → strict parent_ids-anchored pool (unchanged)
   *   spouse      → context-aware: son/daughter-in-law → viewer's children;
   *                 brother/sister-in-law → viewer's siblings; plain →
   *                 every live row; partners without an existing spouse_id
   *                 surfaced first.
   *   unknown +
   *   in-law parent labels (Mother-in-law / Father-in-law) → viewer's
   *                 spouse pool (their parents become your in-laws). We
   *                 surface the viewer's spouse so the user can pick
   *                 them — the engine then derives Mother/Father-in-law
   *                 automatically from the spouse_id chain.
   */
  const candidates = useMemo<GraphMember[]>(() => {
    if (!member) return [];
    
  const visibleMembers = Array.isArray(members)
    ? members.filter((m: any) => m?.approvalStatus !== 'rejected')
    : [];

  const base = visibleMembers.filter(
      (m) => m.id !== member.id && m.approvalStatus !== 'rejected',
    );
    const viewer = effectiveViewerId
      ? members.find((mm) => mm.id === effectiveViewerId)
      : null;
    switch (relationship) {
      case 'co-parent':
        emitGovernorLiveAction(
          'coparent_child_selected',
          {
            source:
              'SetRelationshipModal',
          },
        );

        return base.filter(memberCandidate => {
          const candidateRelationship =
            String(
              memberCandidate.relationship ??
              '',
            ).toLowerCase();

          const candidateParentIds =
            Array.isArray(
              memberCandidate.parentIds,
            )
              ? memberCandidate.parentIds
              : [];

          return (
            candidateRelationship ===
              'child' ||
            (
              effectiveViewerId
                ? candidateParentIds.includes(
                    effectiveViewerId,
                  )
                : false
            )
          );
        });
      case 'cousin':
        return base.filter((m) => m.relationship === 'aunt-uncle');
      case 'cousin-child':
        if (isNieceNephewMode) {
          return base.filter((m) => m.relationship === 'sibling');
        }
        return base.filter((m) => m.relationship === 'cousin');
      case 'aunt-uncle':
        return base.filter(
          (m) =>
            m.relationship === 'mother' ||
            m.relationship === 'father' ||
            m.relationship === 'step-parent' ||
            m.relationship === 'adopted-parent' ||
            m.relationship === 'grandparent',
        );
    
      case 'grandparent':
      case 'great-grandparent':
        /*
         * CC-TREE-LIVE-003G3A
         *
         * Relationship-edit candidate presentation consumes the same
         * canonical authority as Add Member and both lower writers.
         */
        return selectCanonicalTreeGenerationAnchorCandidates({
          members,
          viewerMemberId:
            effectiveViewerId,
          relationship,
          optionKey:
            selectedExtendedKey,
          subjectMemberId:
            member.id,
          side,
        });

      case 'child':
        return base.slice().sort((a, b) => {
          const aIsViewer = effectiveViewerId ? a.id === effectiveViewerId : false;
          const bIsViewer = effectiveViewerId ? b.id === effectiveViewerId : false;
          if (aIsViewer && !bIsViewer) return -1;
          if (bIsViewer && !aIsViewer) return 1;
          return a.name.localeCompare(b.name);
        });
      case 'sibling':
        return base.filter(
          (m) =>
            m.relationship === 'mother' ||
            m.relationship === 'father' ||
            m.relationship === 'step-parent' ||
            m.relationship === 'adopted-parent',
        );
      case 'spouse': {
        // Modern-family in-law derivation. Anchor = the BLOOD relative
        // this in-law is partnered with. The kinship engine then
        // automatically labels the in-law as Son-in-law / Daughter-in-law /
        // Brother-in-law / Sister-in-law from the viewer's perspective
        // (see computeKinship's spouse-hop derivation block).
        let pool = base;
        if (inLawMode === 'son-daughter' && effectiveViewerId) {
          pool = pool.filter((m) =>
            (m.parentIds ?? []).includes(effectiveViewerId),
          );
        } else if (inLawMode === 'brother-sister' && effectiveViewerId) {
          const viewerParents = new Set(
            (viewer?.parentIds ?? []).filter(Boolean),
          );
          if (viewerParents.size > 0) {
            pool = pool.filter(
              (m) =>
                m.id !== effectiveViewerId &&
                (m.parentIds ?? []).some((pid) => viewerParents.has(pid)),
            );
          }
        }
        return pool.slice().sort((a, b) => {
          const aHasSpouse = a.spouseId ? 1 : 0;
          const bHasSpouse = b.spouseId ? 1 : 0;
          if (aHasSpouse !== bHasSpouse) return aHasSpouse - bHasSpouse;
          return a.name.localeCompare(b.name);
        });
      }
      case 'unknown':
        // Mother-in-law / Father-in-law / Godparent / Family Friend etc.
        // For parent-in-law variants we surface the viewer's spouse FIRST
        // so the user can anchor to them — the kinship engine then
        // auto-derives "Mother-in-law" / "Father-in-law" via the
        // spouse-hop chain.
        if (inLawMode === 'parent') {
          return base.slice().sort((a, b) => {
            const aIsSpouse = viewer?.spouseId === a.id ? 0 : 1;
            const bIsSpouse = viewer?.spouseId === b.id ? 0 : 1;
            if (aIsSpouse !== bIsSpouse) return aIsSpouse - bIsSpouse;
            return a.name.localeCompare(b.name);
          });
        }
        return [];
      default:
        return [];
    }
  }, [
    members,
    member,
    relationship,
    effectiveViewerId,
    isNieceNephewMode,
    inLawMode,
    selectedExtendedKey,
    side,
  ]);

  const candidateLabel = (c: GraphMember): string => {
    if (effectiveViewerId && c.id === effectiveViewerId) return 'You';
    if (c.relationship === 'self') return 'You';
    // Use the engine's perspective-correct label so the user sees
    // "Daughter" / "Sister" / "Spouse" — not the raw stored enum.
    const k = kinshipLabel(c, effectiveViewerId, members);
    return k && k !== 'Unknown relation' ? k : 'Relative';
  };

  // Connector prompt + section copy — mirrors AddGraphMemberModal so
  // the same wording appears across the add flow and the connect flow.
  const connectorMeta = useMemo<{
    show: boolean;
    required: boolean;
    prompt: string;
    empty: string;
  }>(() => {
    switch (relationship) {
      case 'co-parent':
        return {
          show: true,
          required: true,
          prompt:
            "Which child do you share with this co-parent?",
          empty:
            'Add the shared child first so this co-parent can attach beside them.',
        };
      case 'cousin':
        return {
          show: true,
          required: true,
          prompt:
            "Which aunt or uncle is this cousin's parent?",
          empty:
            'Add an aunt or uncle first to connect this cousin to the tree.',
        };
      case 'cousin-child':
        if (isNieceNephewMode) {
          return {
            show: true,
            required: true,
            prompt: "Which sibling of yours is this niece/nephew's parent?",
            empty: 'Add a sibling first so this niece/nephew can sit beside them.',
          };
        }
        return {
          show: true,
          required: true,
          prompt: "Which cousin is this child's parent?",
          empty: 'Add a cousin first so we know which branch this child belongs to.',
        };
      case 'aunt-uncle':
        return {
          show: true,
          required: true,
          prompt:
            'Which parent or grandparent is this person related through? (Pick a parent for aunt/uncle, or a grandparent for great-aunt/great-uncle.)',
          empty: 'Add a parent or grandparent first so this aunt/uncle can sit beside them.',
        };
      case 'grandparent':
        return {
          show: true,
          required: true,
          prompt: 'Which parent is this grandparent the parent of?',
          empty: 'Add a parent first so we know which side this grandparent belongs to.',
        };
      case 'great-grandparent':
        if (
          selectedExtendedKey ===
          'v_great_great_grandparent'
        ) {
          return {
            show: true,
            required: true,
            prompt:
              'Which great-grandparent is this great-great-grandparent the parent of?',
            empty:
              'Connect a great-grandparent first so this additional ancestry generation has an explicit descendant anchor.',
          };
        }

        return {
          show: true,
          required: true,
          prompt:
            'Which grandparent is this great-grandparent the parent of?',
          empty:
            'Connect a grandparent first so this great-grandparent has an explicit descendant anchor.',
        };
      case 'child':
        return {
          show: true,
          required: false,
          prompt: "Who is this child's parent?",
          empty: "Add another relative first to pick as this child's parent.",
        };
      case 'sibling':
        return {
          show: true,
          required: false,
          prompt: 'Which parent(s) do you share?',
          empty: 'Add a parent first so siblings can inherit them.',
        };
      case 'spouse': {
        if (inLawMode === 'son-daughter') {
          return {
            show: true,
            required: true,
            prompt: 'Which of your children is this person partnered with?',
            empty: "Add one of your children first — then we can attach this in-law beside them.",
          };
        }
        if (inLawMode === 'brother-sister') {
          return {
            show: true,
            required: true,
            prompt: 'Which sibling of yours is this person partnered with?',
            empty: 'Add a sibling first so this in-law can sit beside them.',
          };
        }
        return {
          show: true,
          required: true,
          prompt: 'Who is this person married to / partnered with?',
          empty: 'Add the partner first so this spouse can sit beside them.',
        };
      }
      case 'unknown':
        if (inLawMode === 'parent') {
          return {
            show: true,
            required: false,
            prompt: 'Whose parent is this in-law? (Usually your spouse — picking them lets the engine derive "Mother-in-law" / "Father-in-law" automatically.)',
            empty: "Add your spouse first — then we can derive the in-law label automatically.",
          };
        }
        return { show: false, required: false, prompt: '', empty: '' };
      default:
        return { show: false, required: false, prompt: '', empty: '' };
    }
  }, [
    relationship,
    isNieceNephewMode,
    inLawMode,
    selectedExtendedKey,
  ]);

  const supportsDualParent =
    relationship === 'child' ||
    relationship === 'cousin' ||
    relationship === 'cousin-child' ||
    relationship === 'sibling';

  const secondCandidates = useMemo(
    () => candidates.filter((c) => c.id !== connectorId),
    [candidates, connectorId],
  );

  // ─── Live derived-label PREVIEW ────────────────────────────────────────
  // "<member.name> will connect as: <derived label> through <connector
  // name>." Computes the engine's perspective-correct label by
  // simulating the spouse_id / parent_ids edge the save will write.
  // Pure read-only — never mutates anything. Falls back to the raw
  // customLabel when the connector is empty.
  const previewLabel = useMemo<string | null>(() => {
    if (!member || !effectiveViewerId) return null;
    if (!connectorId) {
      return customLabel.trim() || null;
    }
    // Build a hypothetical members list with the proposed edge applied
    // so kinshipLabel returns the post-save label rather than the
    // current state. Pure projection — never mutates the real list.
    const simulated = members.map((m) => {
      if (relationship === 'spouse' && m.id === member.id) {
        return { ...m, spouseId: connectorId, relationshipOverride: customLabel.trim() || m.relationshipOverride };
      }
      if (relationship === 'spouse' && m.id === connectorId) {
        return { ...m, spouseId: member.id };
      }
      // Bloodline branches — write parent_ids onto the subject row.
      if (
        m.id === member.id &&
        (relationship === 'cousin' ||
          relationship === 'cousin-child' ||
          relationship === 'child' ||
          relationship === 'sibling')
      ) {
        const parents = [connectorId];
        if (secondConnectorId && secondConnectorId !== connectorId) {
          parents.push(secondConnectorId);
        }
        return { ...m, parentIds: parents };
      }
      if (m.id === member.id && relationship === 'aunt-uncle') {
        const conn = members.find((mm) => mm.id === connectorId);
        const inherited = (conn?.parentIds ?? []).filter(Boolean);
        if (inherited.length > 0) {
          return { ...m, parentIds: inherited };
        }
      }
      return m;
    });
    try {
      return kinshipLabel(
        simulated.find((mm) => mm.id === member.id)!,
        effectiveViewerId,
        simulated,
      );
    } catch {
      return customLabel.trim() || null;
    }
  }, [
    member,
    effectiveViewerId,
    members,
    relationship,
    connectorId,
    secondConnectorId,
    customLabel,
  ]);

  const connectorName = useMemo(() => {
    if (!connectorId) return null;
    const c = members.find((m) => m.id === connectorId);
    return c?.name ?? null;
  }, [connectorId, members]);

  // BUG 1 FIX — keep all hooks above any early-return so React's
  // hook-order check stays stable.
  if (!member) return null;

  // ─── Viewer-anchor health for THIS save ──────────────────────────────
  // Parent-edge saves write to the viewer's parent_ids — they cannot
  // succeed without a real viewer member id. Surface the repairing
  // banner + disable the submit button so the user never gets a silent
  // edge-insert failure. The FamilyGraphContext bootstrap repair ladder
  // populates effectiveViewerId on most loads; this UI guard is the
  // last-line defence for the rare edge case where it doesn't.
  const requiresViewerAnchor =
    relationship === 'mother' || relationship === 'father';
  const viewerAnchorMissing = requiresViewerAnchor && !effectiveViewerId;

  /**
   * Apply the relationship + side update + connector edge.
   *
   * For spouse-coreRelationship rows we ONLY call linkSpouses(member,
   * connector). We never call linkParents(viewer, member) because an
   * in-law is the viewer's CHILD'S spouse — not the viewer's child.
   * The kinship engine derives "Son-in-law" / "Daughter-in-law" from
   * the spouse_id chain at render time.
   *
   * For bloodline rows the original strict parent_ids logic is kept
   * unchanged.
   */
  const save = async () => {
    setSaving(true);
    try {

      const connector = connectorId
        ? members.find((m) => m.id === connectorId) ?? null
        : null;
      const secondConnector =
        supportsDualParent && secondConnectorId && secondConnectorId !== connectorId
          ? members.find((m) => m.id === secondConnectorId) ?? null
          : null;

      // ─── PERMANENT EDGE-INSERT DIAGNOSTICS ────────────────────────────
      // CRITICAL FIX (May 2026 task: "authenticated users are not
      // resolving to a viewer family member node before edge creation").
      // Logs the four signals diagnostics confirmed were missing at
      // edge-insert time so any future "edge insert failed silently"
      // report can be traced from the browser console without guesswork.
      // eslint-disable-next-line no-console
      console.log('[KinsCircle] SetRelationshipModal.save', {
        viewerMemberId,
        effectiveViewerId,
        selectedAnchor: connector?.id ?? null,
        secondAnchor: secondConnector?.id ?? null,
        relationship,
        selectedExtendedKey,
        memberId: member.id,
        memberName: member.name,
        edgePayload: {
          memberId: member.id,
          relationship,
          parentIds:
            relationship === 'cousin' ||
            relationship === 'cousin-child' ||
            relationship === 'child' ||
            relationship === 'sibling' ||
            relationship === 'aunt-uncle'
              ? [
                  ...(connector?.id ? [connector.id] : []),
                  ...(secondConnector?.id ? [secondConnector.id] : []),
                ]
              : null,
          spouseLink:
            relationship === 'spouse' && connector ? connector.id : null,
        },
      });

      // ─── HARD GUARD: viewer mapping required for parent-anchored saves ──
      // Mother / Father saves write to the VIEWER's parent_ids — without
      // a real viewer member id we'd silently drop the edge insert. The
      // FamilyGraphContext bootstrap repair ladder should have populated
      // effectiveViewerId by now; if it's still null something upstream
      // failed and we MUST surface it instead of returning a misleading
      // success toast.
      if (
        (relationship === 'mother' || relationship === 'father') &&
        !effectiveViewerId
      ) {
        // eslint-disable-next-line no-console
        console.error(
          '[KinsCircle] SetRelationshipModal: viewer anchor missing — refusing parent-edge save',
          { viewerMemberId, effectiveViewerId },
        );
        toast.error(
          'Unable to determine your family anchor. Repairing connection — please refresh and try again.',
        );
        setSaving(false);
        return;
      }

      // Required-connector guard.
      if (connectorMeta.show && connectorMeta.required && !connector) {
        toast.error(
          `Please pick the relative this person connects through to save.`,
        );
        setSaving(false);
        return;
      }

      /*
       * CC-TREE-LIVE-003G3A
       *
       * Revalidate the selected ancestor anchor immediately before the
       * confirmation and before any row relabeling / topology mutation.
       * Candidate-list eligibility alone is never write authority.
       */
      const generationAnchorDecision =
        resolveCanonicalTreeGenerationAnchorAuthority({
          members,
          viewerMemberId:
            effectiveViewerId,
          relationship,
          optionKey:
            selectedExtendedKey,
          anchorMemberId:
            connector?.id ?? null,
          subjectMemberId:
            member.id,
          side,
          relationshipOverride:
            customLabel.trim() ||
            null,
        });

      if (
        generationAnchorDecision.applies &&
        !generationAnchorDecision.authorized
      ) {
        toast.error(
          generationAnchorDecision.reason,
        );
        setSaving(false);
        return;
      }

      // SAFETY PREVIEW — confirm before changing relationship edges.
      const previewName = member.name || 'this person';
      const previewLabel =
        customLabel.trim() ||
        selectedExtendedKey ||
        relationship;

      const throughName = connector?.name ? ` through ${connector.name}` : '';
      const ok = window.confirm(
        `Confirm relationship update:\n\n${previewName} will be saved as: ${previewLabel}${throughName}.\n\nThis may update relationship / side / parent or spouse links. Continue?`
      );

      if (!ok) {
        setSaving(false);
        return;
      }

      // Build the same governed workflow descriptor used by Add Member.
      // This supplements the backward-compatible core relationship enum and
      // never invents parent_ids or spouse_id edges.
      const workflowOption =
        findExtendedOption(selectedExtendedKey) ??
        findExtendedOption(customLabel.trim()) ??
        findExtendedOption(relationship);

      const creationPolicy =
        resolveCanonicalTreeRelationshipCreationPolicy(
          selectedExtendedKey,
        );

      if (creationPolicy.disposition !== 'CREATE') {
        toast.error(creationPolicy.reason);
        setSaving(false);
        return;
      }

      const relationshipWorkflow = workflowOption
        ? buildRelationshipWorkflow(
            workflowOption,
            connectorId || null,
            secondConnectorId || null,
          )
        : null;

      const ancestryReconnectionPlan =
        connector
          ? relationship === 'sibling'
            ? planCanonicalTreeAncestryReconnection({
                members,
                subjectMemberId:
                  member.id,
                intent:
                  'SET_EXPLICIT_SUBJECT_PARENTS',
                explicitParentIds:
                  [
                    connector.id,
                    ...(
                      secondConnector
                        ? [secondConnector.id]
                        : []
                    ),
                  ],
              })
            : relationship === 'aunt-uncle'
              ? planCanonicalTreeAncestryReconnection({
                  members,
                  subjectMemberId:
                    member.id,
                  intent:
                    'CONNECT_EXPLICIT_SIBLINGS',
                  anchorMemberId:
                    connector.id,
                })
              : relationship === 'grandparent' ||
                  relationship === 'great-grandparent'
                ? planCanonicalTreeAncestryReconnection({
                    members,
                    subjectMemberId:
                      member.id,
                    intent:
                      'ADD_SUBJECT_AS_PARENT',
                    anchorMemberId:
                      connector.id,
                  })
                : null
          : null;

      if (
        ancestryReconnectionPlan &&
        !ancestryReconnectionPlan.authorized
      ) {
        toast.error(
          ancestryReconnectionPlan.reason,
        );
        setSaving(false);
        return;
      }

      // 1. Update the row's structural relationship + side + label
      //    override IFF anything changed.
      const updates: Partial<GraphMember> = {};
      if (relationship !== member.relationship) updates.relationship = relationship;
      if (side !== member.side) updates.side = side;
      const desiredOverride = customLabel.trim() || null;
      if ((member.relationshipOverride ?? null) !== desiredOverride) {
        updates.relationshipOverride = desiredOverride;
      }

      const existingWorkflow =
        member.relationshipWorkflow ?? null;

      if (
        JSON.stringify(existingWorkflow) !==
        JSON.stringify(relationshipWorkflow)
      ) {
        updates.relationshipWorkflow =
          relationshipWorkflow;
      }

      if (Object.keys(updates).length > 0) {
        await updateMember(member.id, updates);
      }

      let connectionApplied = false;

      // 2. SPOUSE / IN-LAW BRANCH — link via spouse_id, NOT parent_ids.
      //    This is the key change: selecting "Son-in-law" anchors the
      //    in-law's spouse_id to the picked child of yours. The
      //    kinship engine derives the in-law label automatically.
      if (relationship === 'co-parent' && connector) {
        const existing = (connector.parentIds ?? []).filter((p) => p !== member.id);
        await linkParents(connector.id, [member.id, ...existing]);
        connectionApplied = true;
      }

      // CO-PARENT BRANCH complete: selected child receives this co-parent as a parent edge.

      if (relationship === 'spouse' && connector) {
        try {
          await linkSpouses(member.id, connector.id);
          connectionApplied = true;
        } catch (err: any) {
          toast.error(err?.message || 'Could not link this spouse / partner.');
          setSaving(false);
          return;
        }
      }

      // 3. MOTHER / FATHER BRANCH — append to viewer.parent_ids.
      if (
        (relationship === 'mother' || relationship === 'father') &&
        !connectionApplied
      ) {
        const viewerId = effectiveViewerId;
        if (!viewerId) {
          toast.error(
            'Unable to determine your family anchor. Repairing connection — please refresh and try again.',
          );
          setSaving(false);
          return;
        }
        const viewer = members.find((m) => m.id === viewerId) ?? null;
        if (!viewer) {
          toast.error(
            'Unable to determine your family anchor. Repairing connection — please refresh and try again.',
          );
          setSaving(false);
          return;
        }
        if (viewer.id === member.id) {
          toast.error('You cannot mark your own profile as your parent.');
          setSaving(false);
          return;
        }
        const liveById = new Map(members.map((m) => [m.id, m]));
        const existing = (viewer.parentIds ?? []).filter((p) => {
          if (p === member.id) return false;
          const row = liveById.get(p);
          return !!row && row.approvalStatus !== 'rejected';
        });
        await linkParents(viewer.id, [member.id, ...existing]);
        connectionApplied = true;
      }

      // 4. GOVERNED ANCESTRY RECONNECTION
      // Parent-edge plans execute only through linkParents, which remains
      // the sole gateway-backed parent mutation path. Unknown-parent
      // explicit siblings persist only through relationshipWorkflow.
      if (
        ancestryReconnectionPlan?.authorized &&
        !connectionApplied
      ) {
        if (
          ancestryReconnectionPlan.status ===
          'AUTHORIZED_PARENT_EDGE_PLAN'
        ) {
          await linkParents(
            ancestryReconnectionPlan
              .parentEdgePlan
              .targetMemberId,
            [
              ...ancestryReconnectionPlan
                .parentEdgePlan
                .proposedParentIds,
            ],
          );
        } else {
          // The workflow descriptor already contains the explicit sibling
          // endpoint. No parent_ids are fabricated.
        }

        connectionApplied = true;
      }

      // 5. REMAINING BLOODLINE BRANCHES
      // Cousin, cousin-child, and child retain their existing explicit
      // connector behavior. Governed sibling and ancestor plans above
      // must never fall through to legacy inference.
      if (connector && !connectionApplied) {
        if (
          relationship === 'cousin' ||
          relationship === 'cousin-child' ||
          relationship === 'child'
        ) {
          const newParents = [connector.id];
          if (secondConnector) newParents.push(secondConnector.id);
          const liveById = new Map(members.map((m) => [m.id, m]));
          const existing = (member.parentIds ?? []).filter((p) => {
            if (newParents.includes(p)) return false;
            const row = liveById.get(p);
            return !!row && row.approvalStatus !== 'rejected';
          });
          await linkParents(member.id, [...newParents, ...existing]);
          connectionApplied = true;
        }
      }

      // 5. UNKNOWN + parent-in-law connector → engine derives the
      //    label via the spouse-hop chain when the connector is the
      //    viewer's spouse. Nothing structural to write here, but we
      //    DO save the override so the in-law title persists.
      const savedAsNeedsConnection =
        connectorMeta.show && !connectionApplied && relationship !== 'child' &&
        relationship !== 'unknown';

      try {
        await refresh();
      } catch (err) {
        console.warn('[SetRelationshipModal] post-save refresh failed', err);
      }

      if (savedAsNeedsConnection) {
        toast.warning(
          `${member.name} saved — pick a relative above to attach them in the tree.`,
        );
      } else {
        const labelForToast = previewLabel || customLabel.trim() || 'connected';
        toast.success(`${member.name} connected as ${labelForToast}.`);
      }
      onOpenChange(false);
    } catch (err: any) {
      console.error('[SetRelationshipModal] save FAILED', err);
      toast.error(err?.message || 'Could not update relationship.');
    } finally {
      setSaving(false);
    }
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-xl">
            <Link2 className="w-5 h-5 text-emerald-600" />
            Connect to your tree
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="flex items-center gap-3 rounded-xl bg-slate-50 border border-slate-200 p-3">
            <MemberAvatar
              name={member.name}
              photoUrl={member.photoUrl}
              size="md"
              verified={member.approvalStatus === 'approved'}
              pending={member.approvalStatus === 'pending'}
            />
            <div className="min-w-0">
              <div className="font-semibold text-slate-900 truncate">{member.name}</div>
              <div className="text-xs text-slate-600">
                Set how this person connects to your family tree. Their photos,
                memories, and history are kept exactly as they are.
              </div>
            </div>
          </div>

          <div>
            <Label>Relationship to you</Label>
            {/*
              UNIFIED EXTENDED-OPTIONS DROPDOWN — same source of truth as
              AddGraphMemberModal and the kinship engine. Uses Radix
              SelectGroup + SelectLabel for the section headers (rather
              than bare <div> children) so EVERY option renders reliably
              across browsers and Radix versions. The previous bare-div
              approach was the root cause of users only seeing the
              first ~11 entries (Mother → Step-Parent) and never
              reaching the In-laws / Step / Other groups — including the
              Son-in-law / Daughter-in-law / Brother-in-law /
              Sister-in-law / Mother-in-law / Father-in-law / Step-Child
              entries the kinship engine derives from.

              Section copy follows the spec exactly:
                • Core family
                • Extended family
                • Step / blended family
                • In-laws & partners
                • Other / chosen family

              Selecting Son-in-law / Daughter-in-law sets
              coreRelationship='spouse' + displayOverride='Son-in-law' /
              'Daughter-in-law', and the connector picker auto-filters
              to the viewer's children (see `inLawMode === 'son-daughter'`
              above). The save handler then writes ONLY spouse_id to the
              picked child — never parent_ids on the viewer — which is
              what makes Terrance Lewis Sr. anchor beside Chekesha and
              vanish from "Needs Connection" with the heart connector.
            */}
            <Select
              value={selectedExtendedKey}
              onValueChange={(v) => applyExtendedKey(v)}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-[60vh]">
                <SelectGroup>
                  <SelectLabel className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Core family
                  </SelectLabel>
                  {EXTENDED_RELATIONSHIP_OPTIONS.filter((o) => o.group === 'core').map((r) => (
                    <SelectItem
  key={r.value}
  value={r.value}
  disabled={
    resolveCanonicalTreeRelationshipCreationPolicy(
      r.value,
    ).disposition !== 'CREATE'
  }
>
  {r.label}
</SelectItem>
                  ))}
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Extended family
                  </SelectLabel>
                  {EXTENDED_RELATIONSHIP_OPTIONS.filter((o) => o.group === 'extended').map((r) => (
                    <SelectItem
  key={r.value}
  value={r.value}
  disabled={
    resolveCanonicalTreeRelationshipCreationPolicy(
      r.value,
    ).disposition !== 'CREATE'
  }
>
  {r.label}
</SelectItem>
                  ))}
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Step / blended family
                  </SelectLabel>
                  {EXTENDED_RELATIONSHIP_OPTIONS.filter((o) => o.group === 'step').map((r) => (
                    <SelectItem
  key={r.value}
  value={r.value}
  disabled={
    resolveCanonicalTreeRelationshipCreationPolicy(
      r.value,
    ).disposition !== 'CREATE'
  }
>
  {r.label}
</SelectItem>
                  ))}
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel className="text-[10px] font-bold uppercase tracking-wider text-fuchsia-700">
                    In-laws & partners
                  </SelectLabel>
                  {EXTENDED_RELATIONSHIP_OPTIONS.filter((o) => o.group === 'in-law').map((r) => (
                    <SelectItem
  key={r.value}
  value={r.value}
  disabled={
    resolveCanonicalTreeRelationshipCreationPolicy(
      r.value,
    ).disposition !== 'CREATE'
  }
>
  {r.label}
</SelectItem>
                  ))}
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Other / chosen family
                  </SelectLabel>
                  {EXTENDED_RELATIONSHIP_OPTIONS.filter((o) => o.group === 'other').map((r) => (
                    <SelectItem
  key={r.value}
  value={r.value}
  disabled={
    resolveCanonicalTreeRelationshipCreationPolicy(
      r.value,
    ).disposition !== 'CREATE'
  }
>
  {r.label}
</SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            {/* In-law shortcut row — surfaces the four most common
                modern-family labels as one-tap chips so users never have
                to scroll the dropdown to find Son-in-law / Daughter-in-law
                / Brother-in-law / Sister-in-law. Pure visibility helper —
                clicking a chip just calls the same applyExtendedKey()
                that the dropdown does, so the structural save path is
                identical (coreRelationship='spouse' + override label +
                connector picker filtered to the right blood-relative
                pool). */}
            <div className="mt-2 flex flex-wrap gap-1.5">
              {[
                { key: 'v_son_in_law', label: 'Son-in-law' },
                { key: 'v_daughter_in_law', label: 'Daughter-in-law' },
                { key: 'v_brother_in_law', label: 'Brother-in-law' },
                { key: 'v_sister_in_law', label: 'Sister-in-law' },
                { key: 'v_mother_in_law', label: 'Mother-in-law' },
                { key: 'v_father_in_law', label: 'Father-in-law' },
                { key: 'v_step_child', label: 'Step-Child' },
              ].map((chip) => {
                const active = selectedExtendedKey === chip.key;
                  const chipPolicy =
                    resolveCanonicalTreeRelationshipCreationPolicy(
                      chip.key,
                    );
                return (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => applyExtendedKey(chip.key)}
                      disabled={
                        chipPolicy.disposition !== 'CREATE'
                      }
                    className={
                      'rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors ' +
                      (active
                        ? 'bg-fuchsia-600 border-fuchsia-600 text-white'
                        : 'bg-white border-fuchsia-200 text-fuchsia-800 hover:bg-fuchsia-50')
                    }
                  >
                    {chip.label}
                  </button>
                );
              })}
            </div>
          </div>


          <div>
            <Label>Side of the family</Label>
            <Select value={side} onValueChange={(v) => setSide(v as Side)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mother">Mother's side</SelectItem>
                <SelectItem value="father">Father's side</SelectItem>
                <SelectItem value="both">Both / shared</SelectItem>
                <SelectItem value="self">My own side</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500 mt-1">
              In-laws and partners use the side they married into. The
              kinship engine derives the visible label automatically.
            </p>
          </div>

          {/* Connector / anchor picker. */}
          {connectorMeta.show && (
            <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/60 p-3 space-y-2">
              <Label className="text-emerald-900">{connectorMeta.prompt}</Label>
              {candidates.length === 0 ? (
                <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>{connectorMeta.empty}</span>
                </div>
              ) : (
                <Select
                  value={connectorId || '__none__'}
                  onValueChange={(v) => {
                    const next = v === '__none__' ? '' : v;
                    setConnectorId(next);
                    if (supportsDualParent && next) {
                      const picked = members.find((m) => m.id === next);
                      const spouseId = picked?.spouseId ?? '';
                      if (
                        spouseId &&
                        spouseId !== next &&
                        members.some(
                          (m) =>
                            m.id === spouseId &&
                            m.id !== member.id &&
                            m.approvalStatus !== 'rejected',
                        )
                      ) {
                        setSecondConnectorId(spouseId);
                      }
                    }
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Pick a person…" /></SelectTrigger>
                  <SelectContent>
                    {!connectorMeta.required && (
                      <SelectItem value="__none__">— Don't connect yet —</SelectItem>
                    )}
                    {candidates.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} · {candidateLabel(c)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {supportsDualParent && connectorId && candidates.length > 0 && (
                <div className="pt-1">
                  <Label className="text-emerald-900 text-xs">
                    Second parent (optional)
                  </Label>
                  {secondCandidates.length === 0 ? (
                    <p className="text-[11px] text-emerald-800/80 mt-1">
                      Add another possible parent first, then come back to
                      link both. One parent is fine for now.
                    </p>
                  ) : (
                    <Select
                      value={secondConnectorId || '__none__'}
                      onValueChange={(v) =>
                        setSecondConnectorId(v === '__none__' ? '' : v)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Pick the second parent…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— None / unknown —</SelectItem>
                        {secondCandidates.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name} · {candidateLabel(c)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              <p className="text-[11px] text-emerald-800/80">
                {relationship === 'spouse'
                  ? 'In-laws connect via spouse_id to the blood relative they married — they never inherit your bloodline. The label below is derived automatically.'
                  : "Picking someone here only updates the existing relationship link — it never creates duplicate profiles."}
              </p>
            </div>
          )}

          {/* ─── LIVE DERIVED-LABEL PREVIEW ─────────────────────────────
              Mirrors PersonModal / RelativeRoster badges so the user
              sees EXACTLY what the kinship engine will display after
              save — including modern-family labels like "Son-in-law",
              "Daughter-in-law", "Mother-in-law", "Step-Parent",
              "Step-Child", etc. */}
          {previewLabel && (
            <div className="rounded-xl border-2 border-fuchsia-200 bg-fuchsia-50/60 p-3 flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-fuchsia-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-fuchsia-900 leading-snug">
                <strong>{member.name}</strong> will connect as{' '}
                <strong>{previewLabel}</strong>
                {connectorName && relationship === 'spouse' ? (
                  <>
                    {' '}through <strong>{connectorName}</strong>
                  </>
                ) : connectorName && (
                  relationship === 'cousin' ||
                  relationship === 'cousin-child' ||
                  relationship === 'aunt-uncle' ||
                  relationship === 'grandparent' ||
                  relationship === 'great-grandparent' ||
                  relationship === 'sibling' ||
                  relationship === 'child'
                ) ? (
                  <>
                    {' '}via <strong>{connectorName}</strong>
                  </>
                ) : null}
                .
              </div>
            </div>
          )}

          {/* ─── VIEWER-ANCHOR REPAIR WARNING ────────────────────────────
              Surfaces ONLY when a parent-edge save (Mother / Father) is
              about to run and we still don't have a viewer member id.
              The FamilyGraphContext bootstrap repair ladder normally
              populates effectiveViewerId on load — this banner is the
              last-line defence so the user gets a clear "Repairing
              connection..." message instead of a silent edge-insert
              failure. The Save button below is also force-disabled in
              this state. */}
          {viewerAnchorMissing && (

            <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-900 leading-snug">
                <strong>Unable to determine your family anchor.</strong>{' '}
                Repairing connection — please refresh the page and try
                again. (Auth is connected; we just couldn't match you to
                a "You" row in this tree yet.)
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={save}
              disabled={saving || viewerAnchorMissing}
              className="bg-gradient-to-r from-emerald-600 to-teal-500 hover:opacity-95 text-white"
            >
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {relationship === 'spouse' ? (
                <>
                  <Heart className="w-4 h-4 mr-1" />
                  Save partner link
                </>
              ) : (
                'Save placement'
              )}
            </Button>
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SetRelationshipModal;
